import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Spinner, 
  Brain, 
  Compass,
  Trophy,
  Warning,
  Info
} from '@phosphor-icons/react';
import { useHaptic } from '../../../hooks/useHaptic';
import { Button } from '../../../components/ui/Button';
import { FakeProgressBar } from '../../../components/ui/FakeProgressBar';
import { toast } from 'sonner';
import axios from 'axios';
import { getApiBaseUrl } from '../../../core/api/apiClient';

interface ValsSectionProps {
  alumni: any;
  onUpdateAlumni: (updatedData: any) => void;
}

export function ValsSection({ alumni, onUpdateAlumni }: ValsSectionProps) {
  const { lightTap, mediumTap, success: hapticSuccess, error: hapticError } = useHaptic();
  
  const [analyzing, setAnalyzing] = useState(false);
  const [_, setAnalysisStep] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [activeModel, setActiveModel] = useState<string>('');

  const wsRef = useRef<WebSocket | null>(null);

  // Close connection on component unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const result = alumni?.vals_framework || null;

  const handleAnalyze = (isRetryParam?: boolean | any) => {
    const isRetry = typeof isRetryParam === 'boolean' ? isRetryParam : false;
    const alumniId = alumni.donor_id || alumni.source_info?.alumni_id || alumni.source_info?.alumni_csr_id;
    if (!alumniId) {
      hapticError();
      setError('Selected alumnus does not have a valid identifier.');
      return;
    }

    mediumTap();
    setAnalyzing(true);
    setError(null);
    setAnalysisStep(0);
    setActiveModel('');
    setProgressMessage('Establishing connection to search agents...');

    if (wsRef.current) {
      wsRef.current.close();
    }

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = import.meta.env.DEV ? 'localhost:8000' : window.location.host;

    const basePath = (import.meta.env.VITE_BASE_PATH || '/').replace(/\/$/, '');
    const ws = new WebSocket(`${wsProtocol}//${wsHost}${basePath}/api/deepsearch/ws/${alumniId}?force_refresh=true`);
    
    wsRef.current = ws;

    ws.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        if (payload.type === 'progress') {
          setAnalysisStep(payload.step);
          setProgressMessage(payload.message);
          const modelMatch = payload.message?.match(/Scoring with (.+?) —/);
          if (modelMatch) setActiveModel(modelMatch[1]);
          if (payload.message && payload.message.toLowerCase().includes('fallback')) {
            toast.warning('Falling back to next model', {
              description: payload.message,
              duration: 5000,
              icon: '⚠️',
            });
          }
          lightTap();
        } else if (payload.type === 'result') {
          onUpdateAlumni(payload.data);
          setAnalyzing(false);
          setAnalysisStep(0);
          setProgressMessage('');
          hapticSuccess();
        } else if (payload.type === 'error') {
          if (payload.message === 'Authentication required' && !isRetry) {
            axios.post(`${getApiBaseUrl()}/auth/refresh`, {}, { withCredentials: true })
              .then(() => {
                handleAnalyze(true);
              })
              .catch(() => {
                setError('Authentication expired. Please log in again.');
                setAnalyzing(false);
                setAnalysisStep(0);
                setProgressMessage('');
                setActiveModel('');
                hapticError();
              });
            return;
          }
          setError(payload.message);
          setAnalyzing(false);
          setAnalysisStep(0);
          setProgressMessage('');
          setActiveModel('');
          hapticError();
        }
      } catch (err) {
        console.error('Failed to parse WebSocket message:', err);
      }
    };

    ws.onerror = () => {
      setError('Connection lost or failed to connect to analysis server.');
      setAnalyzing(false);
      setAnalysisStep(0);
      setProgressMessage('');
      hapticError();
    };

    ws.onclose = () => {
      setAnalyzing(false);
    };
  };

  const dimensionLabels: Record<string, string> = {
    iitm_affinity: 'IITM Alumni Affinity',
    research_advancement: 'Research Advancement Interest',
    founder_innovation: 'Founder & Innovation Support',
    legacy_recognition: 'Prestige & Legacy Motivation',
    student_development: 'Student Talent Welfare',
    social_national_impact: 'Social & National Impact Focus',
    philanthropic_history: 'Philanthropic Giving History',
    public_influence: 'Public Leadership & Influence',
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 8) return 'bg-emerald-50 border-emerald-100 text-emerald-700';
    if (score >= 5) return 'bg-amber-50 border-amber-100 text-amber-700';
    return 'bg-rose-50 border-rose-100 text-rose-700';
  };

  const getScoreTrackColor = (score: number) => {
    if (score >= 8) return 'bg-emerald-500';
    if (score >= 5) return 'bg-amber-500';
    return 'bg-rose-500';
  };

  const getEvidenceTier = (evidenceBasis?: string) => {
    if (!evidenceBasis) return 'N/A';
    const match = evidenceBasis.match(/Tier\s+[A-C]/i);
    return match ? match[0] : 'N/A';
  };

  const getTierBadgeColor = (tier: string) => {
    if (tier.includes('A')) return 'bg-emerald-50 text-emerald-700 border-emerald-100';
    if (tier.includes('B')) return 'bg-blue-50 text-blue-700 border-blue-100';
    if (tier.includes('C')) return 'bg-slate-50 text-slate-700 border-slate-100';
    return 'bg-slate-50 text-slate-400 border-slate-105';
  };

  return (
    <div className="space-y-6 font-sans mt-8 bg-slate-50 p-6 rounded-2xl border border-slate-200">
      <div className="flex items-center justify-between border-b border-slate-200/60 pb-5">
        <div>
          <h2 className="font-sans font-bold text-lg text-slate-900 leading-none flex items-center gap-2">
            <Brain size={28} className="text-accent" />
            VALS Framework Analysis
          </h2>
          <p className="text-slate-500 text-sm font-medium mt-1">
            Map values, lifestyles, and giving motivations of prospective alumni donors.
          </p>
        </div>
        {!analyzing && !result && (
          <Button
            onClick={handleAnalyze}
            disabled={analyzing || alumni?.research_locked}
            title={alumni?.research_locked ? 'Research is locked by admin' : ''}
            variant="accent"
            className="shadow-sm font-bold flex items-center gap-2"
          >
            {alumni?.research_locked ? 'Locked' : 'Calculate VALS'}
          </Button>
        )}
      </div>

      <AnimatePresence>
        {error && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="p-4 bg-rose-50 border border-rose-100 text-rose-600 rounded-2xl text-xs font-semibold flex items-center gap-3"
          >
            <Warning size={20} className="text-rose-500 shrink-0" />
            {error}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {analyzing && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            className="bg-white border border-slate-200/50 rounded-2xl p-8 shadow-xs flex flex-col items-center justify-center gap-6"
          >
            <div className="relative">
              <Spinner size={36} className="text-accent animate-spin" />
            </div>

            <div className="space-y-4 w-full max-w-md">
              <div className="text-center">
                <h3 className="font-outfit font-bold text-slate-805 text-base">Running Psychographic VALS Profiling</h3>
                <p className="text-xs font-bold text-accent mt-2 animate-pulse min-h-[16px]">{progressMessage || 'Initializing search...'}</p>
                <div className="mt-4 px-4">
                  <FakeProgressBar estimatedTimeMs={180000} isComplete={!analyzing} />
                </div>
              </div>

           
              {activeModel && (
                <motion.div 
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-center mt-3"
                >
                  <div className="inline-flex items-center gap-2.5 px-4 py-2 bg-gradient-to-br from-white to-slate-50/80 border border-slate-200/60 rounded-full shadow-sm backdrop-blur-md transition-all hover:shadow-md hover:border-accent/30">
                    <div className="relative flex h-5 w-5 items-center justify-center">
                      <div className="absolute inset-0 bg-accent/20 rounded-full animate-ping opacity-75"></div>
                      <div className="w-2 h-2 text-accent relative z-10 rounded-full bg-accent" />
                    </div>
                    <span className="text-[13px] font-medium text-slate-500 tracking-wide">
                      Scoring with <span className="font-bold text-slate-800 bg-clip-text bg-gradient-to-r from-accent to-blue-600">{activeModel}</span>
                    </span>
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {result && !analyzing && (
          <motion.div 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6 pt-2"
          >
            {/* Left Column: VALS Scores Chart / Bars */}
            <div className="space-y-6">
              <div className="bg-white border border-slate-200/50 rounded-2xl p-6 shadow-xs space-y-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-sans font-bold text-lg text-slate-900 leading-none">Psychographic VALS Scoreboard</h3>
                    <p className="text-slate-500 text-sm mt-1.5 font-medium">Calibrated ratings mapped across key engagement and giving parameters.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {result.scoring_provider && (
                      <span className="text-[10px] font-bold px-2 py-1 rounded-lg bg-slate-100 border border-slate-200 text-slate-500 uppercase tracking-wide">
                        {result.scoring_provider.replace('-', ' ')}
                      </span>
                    )}
                    <Button
                      onClick={handleAnalyze}
                      disabled={analyzing || alumni?.research_locked}
                      title={alumni?.research_locked ? 'Research is locked by admin' : ''}
                      variant="outline"
                      className="text-xs py-1.5 px-3 h-auto"
                    >
                      {alumni?.research_locked ? 'Locked' : 'Recalculate'}
                    </Button>
                  </div>
                </div>

                <div className="space-y-4">
                  {Object.entries(result.scores || {}).map(([dimKey, value]: [string, any]) => {
                    const label = dimensionLabels[dimKey] || dimKey;
                    const score = value?.score ?? 0;
                    const conf = value?.confidence ?? 50;
                    return (
                      <div key={dimKey} className="space-y-1.5 group font-sans">
                        <div className="flex items-center justify-between text-xs font-semibold">
                          <span className="text-slate-700 font-medium flex items-center gap-1.5">
                            {label}
                            {value?.reason && (
                              <div className="relative group/tooltip">
                                <Info size={14} className="text-slate-400 cursor-help hover:text-accent transition-colors" weight="bold" />
                                <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover/tooltip:block w-72 p-3.5 bg-white border border-slate-100 text-slate-600 text-[11px] rounded-2xl shadow-xl shadow-slate-200/50 z-50 whitespace-normal font-medium leading-relaxed ring-1 ring-slate-900/5">
                                  {value.reason}
                                </div>
                              </div>
                            )}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className={`text-[9px] font-bold px-2 py-0.5 rounded border ${getScoreBgColor(score)}`}>
                              Score: {score}/10
                            </span>
                            <span className="text-slate-450 text-[10px] font-medium">
                              Confidence: {conf}%
                            </span>
                          </div>
                        </div>
                        <div className="w-full bg-slate-100 h-2.5 rounded-full overflow-hidden shadow-inner">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${score * 10}%` }}
                            transition={{ duration: 0.8, ease: 'easeOut' }}
                            className={`h-full rounded-full ${getScoreTrackColor(score)}`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Right Column: Giving blueprint and outreach strategy */}
            <div className="space-y-6">
              {/* Donor Propensity & General Details */}
              <div className="bg-white border border-slate-200/50 rounded-2xl p-6 shadow-xs space-y-4">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-1.5">
                      <h4 className="font-sans font-bold text-lg text-slate-800">Giving Propensity</h4>
                      {result.donation_propensity_reason && (
                        <div className="relative group/tip flex items-center">
                          <Info size={15} className="text-slate-400 cursor-pointer hover:text-accent transition-colors" />
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover/tip:block z-50 w-72 pointer-events-none">
                            <div className="bg-slate-800 text-white text-xs font-medium rounded-xl px-3 py-2.5 leading-relaxed shadow-xl">
                              {result.donation_propensity_reason}
                            </div>
                            <div className="w-2.5 h-2.5 bg-slate-800 rotate-45 absolute bottom-[-5px] left-1/2 -translate-x-1/2" />
                          </div>
                        </div>
                      )}
                    </div>
                    <span className="text-xs text-slate-500 font-medium">Platform confidence score</span>
                  </div>
                  <div className="w-11 h-11 rounded-xl bg-accent-light text-accent font-extrabold text-base flex items-center justify-center border border-accent/20 shadow-sm shrink-0">
                    {result.donation_propensity ?? 0}%
                  </div>
                </div>

                <div className="border-t border-slate-100 pt-4 grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-xs font-medium text-slate-500 block mb-1">Domain</span>
                    <span className="text-sm font-medium text-slate-800 block truncate" title={result.domain}>{result.domain || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-xs font-medium text-slate-500 block mb-1">Financial Tier</span>
                    <span className={`inline-block text-[10px] font-bold border px-2 py-0.5 rounded-md mt-0.5 ${getTierBadgeColor(getEvidenceTier(result.estimated_financial_capacity?.evidence_basis))}`}>
                      {getEvidenceTier(result.estimated_financial_capacity?.evidence_basis)}
                    </span>
                  </div>
                </div>

                {result.confidence && (
                  <div className="border-t border-slate-100 pt-4 flex justify-between items-center text-xs font-bold">
                    <span className="text-slate-500 font-medium">Profile Confidence</span>
                    <span className="font-extrabold text-emerald-600 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded">
                      {result.confidence}%
                    </span>
                  </div>
                )}

                {/* Hobbies / Interests */}
                {result.interests && (
                  <div className="border-t border-slate-100 pt-4 space-y-1.5 font-sans">
                    <span className="text-xs font-medium text-slate-500 block">Individual Hobbies & Interests</span>
                    <p className="text-sm text-slate-700 leading-relaxed bg-slate-50 border border-slate-200/60 p-4 rounded-2xl font-medium">
                      {result.interests}
                    </p>
                  </div>
                )}
              </div>

              {/* Motivation and Strategy */}
              <div className="bg-white border border-slate-200/50 rounded-2xl p-6 shadow-xs space-y-4">
                <h4 className="font-sans font-bold text-lg text-slate-850">Outreach Strategy</h4>

                <div className="space-y-4 font-sans font-medium">
                  <div>
                    <span className="text-xs font-medium text-slate-500 block mb-1.5">Primary Motivation</span>
                    <span className="inline-block text-[10px] font-bold text-accent bg-accent-light border border-accent/20 px-3 py-1 rounded-full uppercase tracking-wider">
                      <Trophy size={12} className="inline mr-1" />
                      {result.primary_motivation || 'General Philanthropy'}
                    </span>
                  </div>

                  <div>
                    <span className="text-xs font-medium text-slate-500 block mb-1.5">Best Outreach Tactic</span>
                    <span className="inline-block text-sm font-medium text-slate-700 leading-relaxed p-4 border border-slate-200/60 rounded-2xl bg-slate-50/50 w-full">
                      <Compass size={14} className="inline mr-1.5 text-accent" />
                      {result.best_outreach_strategy || 'General Relationship Building'}
                    </span>
                  </div>

                  {result.likely_causes && result.likely_causes.length > 0 && (
                    <div>
                      <span className="text-xs font-medium text-slate-500 block mb-2">Likely Cause Buckets</span>
                      <div className="flex flex-wrap gap-1.5">
                        {result.likely_causes.map((c: string) => (
                          <span key={c} className="text-[9px] font-bold text-slate-505 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-lg uppercase tracking-wide">
                            {c.replace('_', ' ')}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Risks and Unknowns */}
              {result.unknowns_or_risks && (
                <div className="bg-white border border-slate-200/50 rounded-2xl p-6 shadow-xs space-y-3">
                  <h4 className="font-sans font-bold text-lg text-slate-800 flex items-center gap-1.5 leading-none">
                    <Warning size={16} className="text-rose-500 shrink-0" />
                    Risks & Key Unknowns
                  </h4>
                  <p className="text-sm text-slate-700 leading-relaxed bg-slate-50 border border-slate-200/60 p-4 rounded-2xl font-medium">
                    {result.unknowns_or_risks}
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
